// YakVender UI kit (FND-C-06): React versions of the design-system components.
// Looks come only from kit.css (the design system's stylesheet) and tokens.css. Feature code composes these
// components and never styles anything itself (guardrail). Every text is passed in by the caller, already translated.
import {
  createContext,
  Fragment,
  useContext,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type KeyboardEvent as ReactKeyboardEvent,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
  type SelectHTMLAttributes,
} from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { Icon, type IconName } from './icons';
import { lineEnter, pulse, reducedMotion, slideFrom } from './motion';

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(' ');

/* ------------------------------------------------------------------ Actions */

type ButtonVariant = 'default' | 'main' | 'quiet' | 'danger';
export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  pill?: boolean;
  block?: boolean;
  /** True while the operation is being posted: the button keeps its width, shows a spinner and cannot be pressed again. */
  busy?: boolean;
  icon?: IconName;
}
export function Button({
  variant = 'default',
  pill,
  block,
  busy,
  icon,
  className,
  children,
  disabled,
  type,
  ...rest
}: ButtonProps) {
  return (
    <button
      {...rest}
      type={type ?? 'button'}
      className={cx(
        'yv-btn',
        variant !== 'default' && `yv-btn--${variant}`,
        pill && 'yv-btn--pill',
        block && 'yv-btn--block',
        className,
      )}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
    >
      {icon && <Icon name={icon} />}
      {children}
    </button>
  );
}

export interface QuickAction {
  id: string;
  label: string;
  icon: IconName;
  main?: boolean;
  onSelect: () => void;
}
/** The page's own operations as large icon tiles. Pass only the actions the current role may run; none renders nothing. */
export function QuickActions({ actions }: { actions: QuickAction[] }) {
  if (actions.length === 0) return null;
  return (
    <div className={cx('yv-quick', actions.length === 1 && 'yv-quick--single')}>
      {actions.map((a) => (
        <button
          key={a.id}
          type="button"
          className={cx('yv-qbtn', a.main && 'yv-qbtn--main')}
          onClick={a.onSelect}
          data-testid={`action-${a.id}`}
        >
          <span className="yv-qbtn__icon">
            <Icon name={a.icon} />
          </span>
          {a.label}
        </button>
      ))}
    </div>
  );
}

/* ------------------------------------------------------------------ Data display */

export interface SummaryFigure {
  label: string;
  value: ReactNode;
}
export function SummaryCard({
  label,
  value,
  sub,
  figures = [],
  plain,
  grid,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  figures?: SummaryFigure[];
  plain?: boolean;
  /** Four figures: two per row (P3). */
  grid?: boolean;
}) {
  // Motion: the main figure pulses once when its value changes after an action (ADR-109).
  const valueRef = useRef<HTMLSpanElement>(null);
  const lastValue = useRef<string | null>(null);
  useEffect(() => {
    const text = valueRef.current?.textContent ?? '';
    if (lastValue.current !== null && lastValue.current !== text) pulse(valueRef.current);
    lastValue.current = text;
  });
  return (
    <section className={cx('yv-summary', plain && 'yv-summary--plain')} aria-label={label}>
      <span className="yv-summary__label">{label}</span>
      <span className="yv-summary__value" ref={valueRef}>
        {value}
      </span>
      {sub !== undefined && <span className="yv-summary__sub">{sub}</span>}
      {figures.length > 0 && (
        <div className={cx('yv-summary__figs', grid && 'yv-summary__figs--grid')}>
          {figures.map((f) => (
            <span className="yv-summary__fig" key={f.label}>
              <b>{f.value}</b>
              <span>{f.label}</span>
            </span>
          ))}
        </div>
      )}
    </section>
  );
}

export function Section({
  title,
  count,
  extra,
  children,
}: {
  title: string;
  count?: number | undefined;
  extra?: ReactNode;
  children: ReactNode;
}) {
  const id = useId();
  return (
    <section aria-labelledby={id}>
      <div className="yv-section__head">
        <h2 className="yv-section__title" id={id}>
          {title}
          {count !== undefined && <span className="yv-count">{count}</span>}
        </h2>
        {extra}
      </div>
      {children}
    </section>
  );
}

/** Lines of a sheet: rows added after the list appeared slide in (ADR-109). */
const NewRowContext = createContext<{ current: boolean } | null>(null);

export function List({
  children,
  label,
  busy,
  animateNew,
}: {
  children: ReactNode;
  label?: string | undefined;
  busy?: boolean | undefined;
  /** Lines being built in a sheet: a row added later slides in. */
  animateNew?: boolean;
}) {
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
  }, []);
  return (
    <NewRowContext.Provider value={animateNew ? mounted : null}>
      <ul className="yv-list" aria-label={label} aria-busy={busy || undefined}>
        {children}
      </ul>
    </NewRowContext.Provider>
  );
}

/** Lists longer than this render only the rows on screen (ADR-109). */
export const VIRTUAL_FROM = 60;

/**
 * A long list (products, register, movements, purchases, customers): up to VIRTUAL_FROM rows render as a plain List;
 * longer lists render only what is on screen, measured row by row so rows keep their real height.
 */
export function VirtualList<T>({
  items,
  label,
  busy,
  rowKey,
  row,
  estimate = 72,
}: {
  items: readonly T[];
  label?: string;
  busy?: boolean;
  rowKey: (item: T) => string | number;
  /** Renders one ListRow. */
  row: (item: T) => ReactNode;
  estimate?: number;
}) {
  if (items.length <= VIRTUAL_FROM) {
    return (
      <List label={label} busy={busy}>
        {items.map((item) => (
          <ListRowSlot key={rowKey(item)}>{row(item)}</ListRowSlot>
        ))}
      </List>
    );
  }
  return <WindowList items={items} label={label} busy={busy} rowKey={rowKey} row={row} estimate={estimate} />;
}
const ListRowSlot = ({ children }: { children: ReactNode }) => <>{children}</>;

function WindowList<T>({
  items,
  label,
  busy,
  rowKey,
  row,
  estimate,
}: {
  items: readonly T[];
  label?: string | undefined;
  busy?: boolean | undefined;
  rowKey: (item: T) => string | number;
  row: (item: T) => ReactNode;
  estimate: number;
}) {
  const listRef = useRef<HTMLUListElement>(null);
  const v = useWindowVirtualizer({
    count: items.length,
    estimateSize: () => estimate,
    overscan: 8,
    scrollMargin: listRef.current?.offsetTop ?? 0,
    getItemKey: (i) => rowKey(items[i] as T),
  });
  const shown = v.getVirtualItems();
  useLayoutEffect(() => {
    const lis = listRef.current?.children;
    if (!lis) return;
    shown.forEach((item, i) => {
      const li = lis[i] as HTMLElement | undefined;
      if (!li) return;
      li.dataset.index = String(item.index);
      v.measureElement(li);
    });
  });
  const first = shown[0];
  const last = shown[shown.length - 1];
  const margin = v.options.scrollMargin;
  return (
    <ul
      ref={listRef}
      className="yv-list"
      aria-label={label}
      aria-busy={busy || undefined}
      style={{
        paddingBlockStart: first ? first.start - margin : 0,
        paddingBlockEnd: last ? v.getTotalSize() - (last.end - margin) : 0,
      }}
    >
      {shown.map((item) => (
        <ListRowSlot key={item.key}>{row(items[item.index] as T)}</ListRowSlot>
      ))}
    </ul>
  );
}

type LeadTone = 'neutral' | 'warning' | 'danger';
export interface ListRowProps {
  title: ReactNode;
  sub?: ReactNode;
  value?: ReactNode;
  badge?: ReactNode;
  /** An icon, the initials of a name (shown round) or a photo thumbnail (PRD-12, decorative). */
  lead?: { icon: IconName } | { initials: string } | { image: string } | undefined;
  leadTone?: LeadTone;
  actions?: ReactNode;
  testId?: string | undefined;
  /** Tree rows (design system: Tree): one to three levels below the top. */
  depth?: 0 | 1 | 2 | 3;
  /** The whole row opens the record (a card page). */
  onOpen?: (() => void) | undefined;
}
export function ListRow({
  title,
  sub,
  value,
  badge,
  lead,
  leadTone = 'neutral',
  actions,
  testId,
  depth = 0,
  onOpen,
}: ListRowProps) {
  const rowRef = useRef<HTMLLIElement>(null);
  const listMounted = useContext(NewRowContext);
  useLayoutEffect(() => {
    if (listMounted?.current) lineEnter(rowRef.current);
    // Only on mount: a row that is already there never moves again.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <li
      ref={rowRef}
      className={cx('yv-row', depth > 0 && `yv-row--depth-${depth}`, onOpen && 'yv-row--open')}
      data-testid={testId}
    >
      <div className="yv-row__main">
        {lead && (
          <span
            className={cx(
              'yv-row__lead',
              'initials' in lead && 'yv-row__lead--avatar',
              'image' in lead && 'yv-row__lead--photo',
              leadTone !== 'neutral' && `yv-row__lead--${leadTone}`,
            )}
            aria-hidden="true"
          >
            {'icon' in lead ? (
              <Icon name={lead.icon} />
            ) : 'image' in lead ? (
              <img src={lead.image} alt="" loading="lazy" decoding="async" />
            ) : (
              lead.initials
            )}
          </span>
        )}
        <div className="yv-row__text">
          <div className="yv-row__title">
            {onOpen ? (
              <button type="button" className="yv-row__open" onClick={onOpen}>
                {title}
              </button>
            ) : (
              title
            )}
          </div>
          {sub !== undefined && <div className="yv-row__sub">{sub}</div>}
        </div>
        {(value !== undefined || badge !== undefined) && (
          <div className="yv-row__end">
            {value !== undefined && <span className="yv-row__value">{value}</span>}
            {badge}
          </div>
        )}
      </div>
      {actions && <div className="yv-row__actions">{actions}</div>}
    </li>
  );
}

export type BadgeTone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'primary';
const BADGE_ICON: Partial<Record<BadgeTone, IconName>> = { success: 'check', warning: 'clock', danger: 'alert' };
/** One or two words of status. The word carries the meaning; colour and icon only support it. */
export function Badge({
  tone = 'neutral',
  children,
  icon = true,
}: {
  tone?: BadgeTone;
  children: ReactNode;
  icon?: boolean;
}) {
  const name = icon ? BADGE_ICON[tone] : undefined;
  return (
    <span className={cx('yv-badge', tone !== 'neutral' && `yv-badge--${tone}`)}>
      {name && <Icon name={name} />}
      {children}
    </span>
  );
}

/** Stock as complete boxes + loose pieces, never added together (GEN-08). A loose-only product shows pieces only. */
export function QuantityToken({
  boxes,
  loose,
  boxed,
  boxLabel,
  pieceLabel,
}: {
  boxes: number;
  loose: number;
  boxed: boolean;
  boxLabel: string;
  pieceLabel: string;
}) {
  return (
    <span className="yv-qty yv-num">
      {boxed && (
        <span className="yv-qty__box" data-testid="qty-boxes">
          {boxes} {boxLabel}
        </span>
      )}
      <span className="yv-qty__loose" data-testid="qty-loose">
        {loose} {pieceLabel}
      </span>
    </span>
  );
}

export function StockTile({
  name,
  quantity,
  sub,
  empty,
  onOpen,
  testId,
  image,
}: {
  name: ReactNode;
  quantity: ReactNode;
  sub?: ReactNode;
  empty?: boolean;
  /** The tile opens the product's stock detail. */
  onOpen?: (() => void) | undefined;
  testId?: string | undefined;
  /** PRD-12: the product's photo link; shown small beside the name (decorative). */
  image?: string | undefined;
}) {
  const body = (
    <>
      {image ? (
        <div className="yv-tile__head">
          <img className="yv-tile__thumb" src={image} alt="" loading="lazy" decoding="async" />
          <div className="yv-tile__name">{name}</div>
        </div>
      ) : (
        <div className="yv-tile__name">{name}</div>
      )}
      {quantity}
      {sub !== undefined && <div className="yv-tile__sub">{sub}</div>}
    </>
  );
  return onOpen ? (
    <button
      type="button"
      className={cx('yv-tile', 'yv-tile--open', empty && 'yv-tile--empty')}
      onClick={onOpen}
      data-testid={testId}
    >
      {body}
    </button>
  ) : (
    <div className={cx('yv-tile', empty && 'yv-tile--empty')} data-testid={testId}>
      {body}
    </div>
  );
}

/** The grid of StockTiles (two columns on phones). */
export function Tiles({ children, label }: { children: ReactNode; label: string }) {
  return (
    <div className="yv-tiles" role="group" aria-label={label}>
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ Navigation */

/** Where a round is in its life. A status display, not a control. */
export function StageBar({ steps, current, label }: { steps: string[]; current: number; label: string }) {
  return (
    <ol className="yv-stages" aria-label={label}>
      {steps.map((s, i) => (
        <li
          key={s}
          className={cx(i < current && 'is-done', i === current && 'is-current')}
          aria-current={i === current ? 'step' : undefined}
        >
          {s}
        </li>
      ))}
    </ol>
  );
}

export interface TabItem {
  id: string;
  label: string;
}
/** Segmented control for views of the same page. Arrow keys move along reading order (mirrored in RTL). */
export function Tabs({
  items,
  value,
  onChange,
  label,
}: {
  items: TabItem[];
  value: string;
  onChange: (id: string) => void;
  label: string;
}) {
  const refs = useRef<(HTMLButtonElement | null)[]>([]);
  const indicator = useRef<HTMLSpanElement>(null);
  const lastBox = useRef<{ left: number; width: number } | null>(null);
  // Motion: the selected background slides from the previous tab to the chosen one (ADR-109). The tab keeps its own
  // background at rest, so contrast never depends on the moving layer.
  useLayoutEffect(() => {
    const tab = refs.current[items.findIndex((t) => t.id === value)];
    const ind = indicator.current;
    if (!tab || !ind) return;
    const box = { left: tab.offsetLeft, width: tab.offsetWidth };
    const prev = lastBox.current;
    lastBox.current = box;
    if (!prev || reducedMotion() || (prev.left === box.left && prev.width === box.width)) return;
    Object.assign(ind.style, {
      left: `${box.left}px`,
      top: `${tab.offsetTop}px`,
      width: `${box.width}px`,
      height: `${tab.offsetHeight}px`,
    });
    ind.hidden = false;
    tab.classList.add('yv-tab--moving');
    slideFrom(ind, prev.left - box.left, prev.width / box.width, () => {
      ind.hidden = true;
      tab.classList.remove('yv-tab--moving');
    });
  }, [items, value]);
  const onKey = (e: ReactKeyboardEvent, i: number) => {
    if (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') return;
    // The nearest dir attribute (set from the business language by applyLanguage) decides; computed style is the fallback.
    const dirAttr = e.currentTarget.closest('[dir]')?.getAttribute('dir');
    const rtl = dirAttr ? dirAttr === 'rtl' : getComputedStyle(e.currentTarget).direction === 'rtl';
    const forward = (e.key === 'ArrowRight') !== rtl;
    const next = (i + (forward ? 1 : -1) + items.length) % items.length;
    const item = items[next];
    if (!item) return;
    onChange(item.id);
    refs.current[next]?.focus();
    e.preventDefault();
  };
  return (
    <div className="yv-tabs" role="tablist" aria-label={label}>
      <span className="yv-tabs__ind" ref={indicator} hidden aria-hidden="true" />
      {items.map((t, i) => (
        <button
          key={t.id}
          ref={(el) => {
            refs.current[i] = el;
          }}
          type="button"
          role="tab"
          className="yv-tab"
          aria-selected={t.id === value}
          tabIndex={t.id === value ? 0 : -1}
          onClick={() => onChange(t.id)}
          onKeyDown={(e) => onKey(e, i)}
        >
          {t.label}
        </button>
      ))}
    </div>
  );
}

export interface NavItem {
  id: string;
  label: string;
  icon: IconName;
}
/** Phone bottom bar: four pages of the role + "more". It only navigates (GEN-17). */
export function BottomNav({
  items,
  current,
  onNavigate,
  more,
  label,
}: {
  items: NavItem[];
  current: string;
  onNavigate: (id: string) => void;
  more: { label: string; onOpen: () => void };
  label: string;
}) {
  return (
    <nav className="yv-nav" aria-label={label}>
      {items.map((it) => (
        <button
          key={it.id}
          type="button"
          className="yv-nav__item"
          aria-current={it.id === current ? 'page' : undefined}
          onClick={() => onNavigate(it.id)}
        >
          <Icon name={it.icon} />
          {it.label}
        </button>
      ))}
      <button type="button" className="yv-nav__item" onClick={more.onOpen}>
        <Icon name="more" />
        {more.label}
      </button>
    </nav>
  );
}

export interface NavGroup {
  caption: string;
  items: NavItem[];
}
/** Desktop menu (and phone drawer): every page the role may open, grouped. */
export function SideNav({
  groups,
  current,
  onNavigate,
  label,
  brand = 'YakVender',
  footer,
}: {
  groups: NavGroup[];
  current: string;
  onNavigate: (id: string) => void;
  label: string;
  brand?: string;
  /** Below the pages: the person's own preferences (ADR-110). */
  footer?: { label: string; icon: IconName; onSelect: () => void } | undefined;
}) {
  return (
    <nav className="yv-sidenav" aria-label={label}>
      <div className="yv-sidenav__brand">
        <span className="yv-sidenav__mark" aria-hidden="true">
          Y
        </span>
        {brand}
      </div>
      {groups
        .filter((g) => g.items.length > 0)
        .map((g) => (
          <div className="yv-sidenav__group" key={g.caption}>
            <div className="yv-sidenav__caption">{g.caption}</div>
            {g.items.map((it) => (
              <button
                key={it.id}
                type="button"
                className="yv-sidenav__item"
                aria-current={it.id === current ? 'page' : undefined}
                onClick={() => onNavigate(it.id)}
              >
                <Icon name={it.icon} />
                {it.label}
              </button>
            ))}
          </div>
        ))}
      {footer && (
        <div className="yv-sidenav__group yv-sidenav__footer">
          <button type="button" className="yv-sidenav__item" onClick={footer.onSelect}>
            <Icon name={footer.icon} />
            {footer.label}
          </button>
        </div>
      )}
    </nav>
  );
}

export function TopBar({
  title,
  roleLabel,
  onMenu,
  menuLabel,
  onBack,
  backLabel,
}: {
  title: ReactNode;
  roleLabel?: string;
  onMenu?: () => void;
  menuLabel: string;
  /** Card pages: a back button replaces the menu button (design system: TopBar). */
  onBack?: (() => void) | undefined;
  backLabel?: string | undefined;
}) {
  return (
    <header className="yv-topbar">
      {onBack && (
        <button type="button" className="yv-topbar__icon yv-topbar__back" aria-label={backLabel} onClick={onBack}>
          <Icon name="back" />
        </button>
      )}
      {!onBack && onMenu && (
        <button type="button" className="yv-topbar__icon yv-shell__menu" aria-label={menuLabel} onClick={onMenu}>
          <Icon name="menu" />
        </button>
      )}
      <h1 className="yv-topbar__title">{title}</h1>
      {roleLabel && <Badge tone="primary">{roleLabel}</Badge>}
    </header>
  );
}

/** Truck, round and working date for truck-driven pages (GEN-14, GEN-16). The caller provides the three controls. */
export function ContextStrip({
  truck,
  round,
  workingDate,
  meta,
}: {
  truck: ReactNode;
  round: ReactNode;
  workingDate: ReactNode;
  meta?: ReactNode;
}) {
  return (
    <div className="yv-context">
      {truck}
      {round}
      {workingDate}
      {meta !== undefined && <div className="yv-context__meta">{meta}</div>}
    </div>
  );
}

/* ------------------------------------------------------------------ Forms */

export interface FieldProps {
  label: string;
  hint?: ReactNode;
  error?: string | undefined;
  children: (ids: { id: string; describedBy: string | undefined; invalid: boolean }) => ReactNode;
}
/** A labelled control with its hint and the server's refusal message under it. */
export function Field({ label, hint, error, children }: FieldProps) {
  const id = useId();
  const hintId = hint !== undefined ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  const describedBy = [hintId, errorId].filter(Boolean).join(' ') || undefined;
  return (
    <div className={cx('yv-field', error && 'yv-field--error')}>
      <label className="yv-field__label" htmlFor={id}>
        {label}
      </label>
      {children({ id, describedBy, invalid: Boolean(error) })}
      {hint !== undefined && (
        <span className="yv-field__hint" id={hintId}>
          {hint}
        </span>
      )}
      {error && (
        <span className="yv-field__error" id={errorId} role="alert">
          <Icon name="alert" />
          {error}
        </span>
      )}
    </div>
  );
}

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Amounts and quantities: tabular digits, left to right, aligned to the end edge. */
  numeric?: boolean;
}
export function Input({ numeric, className, ...rest }: InputProps) {
  return (
    <input
      {...rest}
      className={cx('yv-input', numeric && 'yv-input--num', className)}
      dir={numeric ? 'ltr' : rest.dir}
    />
  );
}

export function Select({ className, children, ...rest }: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <select {...rest} className={cx('yv-select', className)}>
      {children}
    </select>
  );
}

export function FormGrid({ children }: { children: ReactNode }) {
  return <div className="yv-formgrid">{children}</div>;
}

export interface ChipItem {
  id: string;
  label: string;
  off?: boolean;
}
/** Toggle chips for filters or managed lists. `fixed` chips are shown but not pressable. */
export function Chips({
  items,
  selected,
  onToggle,
  fixed = [],
  label,
}: {
  items: ChipItem[];
  selected: string[];
  onToggle: (id: string) => void;
  fixed?: string[];
  label: string;
}) {
  return (
    <div className="yv-chips" role="group" aria-label={label}>
      {items.map((c) =>
        fixed.includes(c.id) ? (
          <span className="yv-chip" key={c.id}>
            {c.label}
          </span>
        ) : (
          <button
            key={c.id}
            type="button"
            className={cx('yv-chip', c.off && 'yv-chip--off')}
            aria-pressed={selected.includes(c.id)}
            onClick={() => onToggle(c.id)}
          >
            {c.label}
          </button>
        ),
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ Feedback */

const NOTICE_ICON: Record<'info' | 'warning' | 'danger', IconName> = {
  info: 'info',
  warning: 'clock',
  danger: 'alert',
};
/** A short message in a screen or sheet. Errors come from the server and say what to do next. Never for success (use Toast). */
export function Notice({
  tone,
  children,
  icon,
}: {
  tone: 'info' | 'warning' | 'danger';
  children: ReactNode;
  icon?: IconName;
}) {
  return (
    <div className={cx('yv-notice', `yv-notice--${tone}`)} role={tone === 'danger' ? 'alert' : 'status'}>
      <Icon name={icon ?? NOTICE_ICON[tone]} />
      <span>{children}</span>
    </div>
  );
}

export function EmptyState({
  icon,
  title,
  text,
  action,
}: {
  icon: IconName;
  title: string;
  text: string;
  action?: ReactNode;
}) {
  return (
    <div className="yv-empty">
      <span className="yv-empty__icon" aria-hidden="true">
        <Icon name={icon} />
      </span>
      <h3 className="yv-empty__title">{title}</h3>
      <p className="yv-empty__text">{text}</p>
      {action}
    </div>
  );
}

/** Placeholder rows shaped like the list that is loading. */
export function SkeletonList({ rows = 3, label }: { rows?: number; label: string }) {
  return (
    <ul className="yv-list" aria-busy="true" aria-label={label}>
      {Array.from({ length: rows }, (_, i) => (
        <li className="yv-row" key={i}>
          <div className="yv-row__main">
            <span className="yv-skeleton yv-skeleton--lead" />
            <div className="yv-row__text yv-skeleton-stack">
              <span className="yv-skeleton yv-skeleton--title" />
              <span className="yv-skeleton yv-skeleton--line" />
            </div>
          </div>
        </li>
      ))}
    </ul>
  );
}

/** Pull to refresh (ADR-109): grows with the finger; "release" once far enough. Nothing shows at rest. */
export function PullIndicator({ pull, ready, label }: { pull: number; ready: boolean; label: string }) {
  if (pull <= 0) return null;
  return (
    <div className="yv-ptr" role="status" style={{ blockSize: pull }}>
      <span className={cx('yv-ptr__icon', ready && 'yv-ptr__icon--ready')} />
      <span className="yv-ptr__text">{label}</span>
    </div>
  );
}

/** PRD-12: a record's photo (product card). Without one, a quiet placeholder; actions sit underneath. */
export function Photo({
  src,
  alt,
  emptyLabel,
  busy,
  children,
}: {
  src?: string | undefined;
  alt: string;
  emptyLabel: string;
  busy?: boolean;
  children?: ReactNode;
}) {
  return (
    <figure className="yv-photo" aria-busy={busy || undefined}>
      {src ? (
        <img className="yv-photo__img" src={src} alt={alt} decoding="async" />
      ) : (
        <div className="yv-photo__empty">
          <Icon name="box" />
          <span>{emptyLabel}</span>
        </div>
      )}
      {children && <figcaption className="yv-photo__actions">{children}</figcaption>}
    </figure>
  );
}

/** Success confirmation after a saved operation, naming the record. */
export function Toast({ children }: { children: ReactNode }) {
  return (
    <div className="yv-toast" role="status">
      <Icon name="check" />
      {children}
    </div>
  );
}

/* ------------------------------------------------------------------ Overlays */

export interface BottomSheetProps {
  title: string;
  sub?: ReactNode;
  open: boolean;
  onClose: () => void;
  /** While posting, Escape and the scrim do not close the sheet. */
  busy?: boolean;
  children: ReactNode;
}
/** Every operation form: slides up on phones, centred on wide screens. Focus moves in on open and back on close. */
export function BottomSheet({ title, sub, open, onClose, busy, children }: BottomSheetProps) {
  const titleId = useId();
  const sheetRef = useRef<HTMLDivElement>(null);
  const lastFocus = useRef<HTMLElement | null>(null);
  useEffect(() => {
    if (!open) return;
    lastFocus.current = document.activeElement as HTMLElement | null;
    const first = sheetRef.current?.querySelector<HTMLElement>('input, select, textarea, button');
    (first ?? sheetRef.current)?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !busy) onClose();
      // Keep keyboard focus inside the open sheet (aria-modal): Tab and Shift+Tab wrap around.
      if (e.key !== 'Tab' || !sheetRef.current) return;
      const items = [
        ...sheetRef.current.querySelectorAll<HTMLElement>(
          'button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])',
        ),
      ].filter((el) => !el.closest('[hidden]'));
      const first = items[0];
      const last = items[items.length - 1];
      if (!first || !last) {
        e.preventDefault();
        return;
      }
      const active = document.activeElement;
      const outside = !sheetRef.current.contains(active);
      if (e.shiftKey && (active === first || outside)) {
        last.focus();
        e.preventDefault();
      } else if (!e.shiftKey && (active === last || outside)) {
        first.focus();
        e.preventDefault();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      lastFocus.current?.focus();
    };
  }, [open, busy, onClose]);

  // Motion: on phones the grip or the title drags the sheet down; released past 120 px or flicked, it closes (ADR-109).
  const drag = useRef<{ y: number; t: number; dy: number } | null>(null);
  const onDragStart = (e: ReactPointerEvent) => {
    if (busy || window.matchMedia?.('(min-width: 720px)').matches) return;
    drag.current = { y: e.clientY, t: e.timeStamp, dy: 0 };
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
  };
  const onDragMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    const sheet = sheetRef.current;
    if (!d || !sheet) return;
    d.dy = Math.max(0, e.clientY - d.y);
    sheet.style.transition = 'none';
    sheet.style.transform = d.dy ? `translateY(${d.dy}px)` : '';
  };
  const onDragEnd = (e: ReactPointerEvent) => {
    const d = drag.current;
    const sheet = sheetRef.current;
    drag.current = null;
    if (!d || !sheet) return;
    const speed = d.dy / Math.max(1, e.timeStamp - d.t);
    if (d.dy > 120 || (d.dy > 24 && speed > 0.6)) {
      onClose();
      return;
    }
    sheet.style.transition = reducedMotion() ? '' : 'transform .2s ease-out';
    sheet.style.transform = '';
  };
  const dragProps = {
    onPointerDown: onDragStart,
    onPointerMove: onDragMove,
    onPointerUp: onDragEnd,
    onPointerCancel: onDragEnd,
  };

  if (!open) return null;
  return (
    <div className="yv-sheet-layer">
      <div className="yv-scrim" onClick={() => !busy && onClose()} data-testid="sheet-scrim" />
      <div className="yv-sheet" role="dialog" aria-modal="true" aria-labelledby={titleId} ref={sheetRef} tabIndex={-1}>
        <div className="yv-sheet__grip" aria-hidden="true" {...dragProps} />
        <h2 className="yv-sheet__title" id={titleId} {...dragProps}>
          {title}
        </h2>
        {sub !== undefined && <p className="yv-sheet__sub">{sub}</p>}
        {children}
      </div>
    </div>
  );
}

export function SheetTotal({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="yv-sheet__total">
      <span>{label}</span>
      <b>{value}</b>
    </div>
  );
}

export function SheetActions({ children }: { children: ReactNode }) {
  return <div className="yv-sheet__actions">{children}</div>;
}

/** A shareable invoice or settlement document styled with the PaperDoc design-system tokens. */
export function PaperDoc({
  businessName,
  address,
  documentTitle,
  number,
  date,
  party,
  stamp,
  children,
}: {
  businessName: ReactNode;
  address?: ReactNode;
  documentTitle: ReactNode;
  number: ReactNode;
  date: ReactNode;
  party?: ReactNode;
  stamp?: ReactNode;
  children: ReactNode;
}) {
  return (
    <article className="yv-paper">
      <header className="yv-paper__head">
        <div>
          <div className="yv-paper__brand">{businessName}</div>
          {address && <div>{address}</div>}
        </div>
        <div className="yv-paper__meta">
          {documentTitle}
          <br />
          {number}
          <br />
          {date}
        </div>
      </header>
      {party && <div className="yv-paper__party">{party}</div>}
      {children}
      {stamp && <div className="yv-paper__stamp">{stamp}</div>}
    </article>
  );
}

export function PaperDocTable({ children }: { children: ReactNode }) {
  return (
    <div className="yv-paper__scroll">
      <table>{children}</table>
    </div>
  );
}

export function PaperTotals({ children }: { children: ReactNode }) {
  return <div className="yv-paper__totals">{children}</div>;
}

export function PaperParty({ children }: { children: ReactNode }) {
  return <div className="yv-paper__party">{children}</div>;
}

/* ------------------------------------------------------------------ P1 patterns (CAT-A-04) */

/** Design system: SearchField. The search box of a list, with an optional scan button. */
export function SearchField({
  value,
  onChange,
  label,
  onScan,
  scanLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  label: string;
  onScan?: (() => void) | undefined;
  scanLabel?: string | undefined;
}) {
  return (
    <div className="yv-search">
      <div className="yv-search__field">
        <Icon name="search" />
        <input
          className="yv-input"
          type="search"
          inputMode="search"
          dir="auto"
          placeholder={label}
          aria-label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </div>
      {onScan && (
        <button type="button" className="yv-btn" aria-label={scanLabel} onClick={onScan}>
          <Icon name="scan" />
        </button>
      )}
    </div>
  );
}

/** The filter bar above a list: search, selects and chips stacked (design system: SearchField). */
export function Filters({ children }: { children: ReactNode }) {
  return <div className="yv-filters">{children}</div>;
}

/** Design system: Scanner. The camera viewfinder; the caller puts the video inside and always offers typing. */
export function Scanner({ hint, children }: { hint: string; children?: ReactNode }) {
  return (
    <div className="yv-scan" role="img" aria-label={hint}>
      {children}
      <span className="yv-scan__frame" />
      <span className="yv-scan__hint">{hint}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ P2 patterns (STK-A-04) */

/** Design system: QuantityInput. Boxes and loose pieces typed side by side; a loose-only product shows pieces only. */
export function QuantityInput({
  boxes,
  loose,
  boxed,
  onBoxes,
  onLoose,
  boxLabel,
  pieceLabel,
  invalid,
  describedBy,
}: {
  boxes: string;
  loose: string;
  boxed: boolean;
  onBoxes: (value: string) => void;
  onLoose: (value: string) => void;
  boxLabel: string;
  pieceLabel: string;
  invalid?: boolean | undefined;
  describedBy?: string | undefined;
}) {
  return (
    <div className="yv-qtyin">
      {boxed && (
        <label className="yv-unit-input">
          <input
            className="yv-input yv-input--num"
            dir="ltr"
            inputMode="numeric"
            aria-label={boxLabel}
            value={boxes}
            onChange={(e) => onBoxes(e.target.value)}
            aria-invalid={invalid || undefined}
            aria-describedby={describedBy}
          />
          <span className="yv-unit-input__unit" aria-hidden="true">
            {boxLabel}
          </span>
        </label>
      )}
      <label className="yv-unit-input">
        <input
          className="yv-input yv-input--num"
          dir="ltr"
          inputMode="numeric"
          aria-label={pieceLabel}
          value={loose}
          onChange={(e) => onLoose(e.target.value)}
          aria-invalid={invalid || undefined}
          aria-describedby={describedBy}
        />
        <span className="yv-unit-input__unit" aria-hidden="true">
          {pieceLabel}
        </span>
      </label>
    </div>
  );
}

/** Design system: LineList — the dashed block where a line is prepared before it is added. */
export function LineAdd({ children }: { children: ReactNode }) {
  return <div className="yv-lineadd">{children}</div>;
}

/* ------------------------------------------------------------------ P4 patterns (PUR-A-04) */

/** Design system: Progress. A thin bar with its count beside it (received ÷ ordered pieces). */
export function Progress({ done, total, label, name }: { done: number; total: number; label: string; name: string }) {
  const pct = total > 0 ? Math.min(100, Math.round((done * 100) / total)) : 0;
  return (
    <div
      className={cx('yv-progress', total > 0 && done >= total && 'yv-progress--done')}
      role="progressbar"
      aria-valuenow={done}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-label={name}
    >
      <span className="yv-progress__track">
        <span className="yv-progress__bar" style={{ width: `${pct}%` }} />
      </span>
      <span className="yv-progress__label">{label}</span>
    </div>
  );
}

/* ------------------------------------------------------------------ P11 patterns (Chart, DataTable) */

export interface ChartBar {
  key: string;
  label: ReactNode;
  value: number;
  /** The formatted value shown at the end of the bar. */
  text: string;
  tone?: 'alt' | 'danger';
}
/** Chart (bars): one value per row. States its basis; the numbers also go in a DataTable after it. */
export function BarChart({
  title,
  basis,
  summary,
  bars,
}: {
  title: string;
  basis?: string | undefined;
  summary: string;
  bars: ChartBar[];
}) {
  const max = Math.max(0, ...bars.map((b) => Math.abs(b.value)));
  return (
    <section className="yv-chart">
      <div className="yv-chart__head">
        <h3 className="yv-chart__title">{title}</h3>
        {basis && <span className="yv-chart__basis">{basis}</span>}
      </div>
      <div className="yv-chart__bars" role="img" aria-label={summary}>
        {bars.map((b) => (
          <div key={b.key} className="yv-chart__bar">
            <span className="yv-chart__label">{b.label}</span>
            <span className="yv-chart__track">
              <span
                className={cx('yv-chart__fill', b.tone && `yv-chart__fill--${b.tone}`)}
                style={{ width: `${max > 0 ? Math.round((100 * Math.abs(b.value)) / max) : 0}%` }}
              />
            </span>
            <span className="yv-chart__value">{b.text}</span>
          </div>
        ))}
      </div>
    </section>
  );
}

/** Chart (columns): a value per day over the period, with an optional second series. */
export function ColumnChart({
  title,
  basis,
  summary,
  values,
  second,
  axis,
  legend,
}: {
  title: string;
  basis: string;
  summary: string;
  values: number[];
  second?: number[] | undefined;
  axis: [string, string];
  legend?: [string, string] | undefined;
}) {
  const max = Math.max(0, ...values.map(Math.abs), ...(second ?? []).map(Math.abs));
  const h = (v: number) => `${max > 0 ? Math.max(2, Math.round((100 * Math.abs(v)) / max)) : 2}%`;
  return (
    <section className="yv-chart">
      <div className="yv-chart__head">
        <h3 className="yv-chart__title">{title}</h3>
        <span className="yv-chart__basis">{basis}</span>
      </div>
      <div className="yv-chart__cols" role="img" aria-label={summary}>
        {values.map((v, i) => (
          <Fragment key={i}>
            <span className="yv-chart__col" style={{ height: h(v) }} />
            {second && <span className="yv-chart__col yv-chart__col--alt" style={{ height: h(second[i] ?? 0) }} />}
          </Fragment>
        ))}
      </div>
      <div className="yv-chart__axis">
        <span>{axis[0]}</span>
        <span>{axis[1]}</span>
      </div>
      {legend && (
        <div className="yv-chart__legend">
          <span className="yv-chart__key">{legend[0]}</span>
          <span className="yv-chart__key yv-chart__key--alt">{legend[1]}</span>
        </div>
      )}
    </section>
  );
}

export interface TableColumn {
  key: string;
  label: string;
  /** Numbers are end-aligned and never wrap. */
  num?: boolean;
}
/** DataTable: the numbers of a report or chart; scrolls inside its frame on phones; a total row closes it. */
export function DataTable({
  label,
  columns,
  rows,
  total,
  warn,
}: {
  label: string;
  columns: TableColumn[];
  rows: { key: string; cells: ReactNode[] }[];
  total?: ReactNode[] | undefined;
  /** Cells that need attention (row key + column key); their heading says why. */
  warn?: (row: string, column: string) => boolean;
}) {
  return (
    // The frame scrolls sideways on phones: it takes focus so the keyboard can scroll it too.
    <div className="yv-table-wrap" tabIndex={0} role="region" aria-label={label}>
      <table className="yv-table" aria-label={label}>
        <thead>
          <tr>
            {columns.map((c) => (
              <th key={c.key} className={c.num ? 'num' : undefined}>
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.key}>
              {r.cells.map((cell, i) => {
                const col = columns[i];
                return (
                  <td
                    key={col?.key ?? i}
                    className={cx(col?.num && 'num', col && warn?.(r.key, col.key) && 'yv-table__warn') || undefined}
                  >
                    {cell}
                  </td>
                );
              })}
            </tr>
          ))}
          {total && (
            <tr className="yv-table__total">
              {total.map((cell, i) => (
                <td key={columns[i]?.key ?? i} className={columns[i]?.num ? 'num' : undefined}>
                  {cell}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

/** True on the desktop layout (side navigation, ≥ 1080 px): a few screens show more at once there (P11 filters). */
export function useWide(): boolean {
  const query = '(min-width: 1080px)';
  const [wide, setWide] = useState(() => typeof window !== 'undefined' && window.matchMedia?.(query).matches === true);
  useEffect(() => {
    const m = window.matchMedia?.(query);
    if (!m) return;
    const on = () => setWide(m.matches);
    m.addEventListener('change', on);
    return () => m.removeEventListener('change', on);
  }, []);
  return wide;
}
